import Vue from "vue";
import VueRouter from "vue-router";

import store from "@/store";
import { FLOW_ORDER } from "@/constants";
import Start from "@/views/start/start.vue";

const { push } = VueRouter.prototype;
const { isNavigationFailure, NavigationFailureType } = VueRouter;

/**
 * Swallow the "redundant navigation to current location" rejection so pushing the same
 * route twice is a no-op rather than an unhandled rejection. Everything else re-throws.
 */
VueRouter.prototype.push = function (location) {
  return push.call(this, location).catch((error) => {
    if (!isNavigationFailure(error, NavigationFailureType.duplicated)) throw error;
  });
};

Vue.use(VueRouter);

/**
 * The reachable flow, and nothing else.
 *
 * `meta.step` ties a route to its position in FLOW_ORDER; the guard below uses it to stop
 * a deep link (or a stale tab) landing on a step whose inputs do not exist yet.
 */
const routes = [
  {
    path: "/",
    name: "start",
    component: Start,
  },
  {
    path: "/capture-image",
    name: "capture-image",
    component: () => import(/* webpackChunkName: "flow" */ "@/views/capture-image/capture-image.vue"),
    meta: { requiresAuth: true, step: "capture-image" },
  },
  {
    path: "/capture-video",
    name: "capture-video",
    component: () => import(/* webpackChunkName: "flow" */ "@/views/capture-video/capture-video.vue"),
    meta: { requiresAuth: true, step: "capture-video" },
  },
  {
    path: "/sign",
    name: "select-sign",
    component: () => import(/* webpackChunkName: "flow" */ "@/views/select-sign/select-sign.vue"),
    meta: { requiresAuth: true, step: "select-sign" },
  },
  {
    path: "/check",
    name: "check-assets",
    component: () => import(/* webpackChunkName: "flow" */ "@/views/check-assets/check-assets.vue"),
    meta: { requiresAuth: true, step: "check-assets" },
  },
  {
    path: "/complete",
    name: "complete",
    component: () => import(/* webpackChunkName: "complete" */ "@/views/complete/complete.vue"),
    meta: { requiresAuth: true, requiresComplete: true },
  },
  { path: "*", redirect: "/" },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

/**
 * One guard, two jobs.
 *
 * Auth used to be a redirect inside App.vue's `mounted()`, which fired only after the
 * protected view had already been created — meaning its own `mounted` had opened the
 * camera and fired requests before anyone checked whether there was a session.
 *
 * The order check is the other half: without it, reloading on /sign after clearing
 * storage would show a signature pad for a flow that has no photo behind it.
 */
router.beforeEach((to, from, next) => {
  if (!to.meta.requiresAuth) return next();

  // The result screen outlives the session: submitting spends the access token, but the
  // user must still be able to read their tracking code and hand back afterwards.
  if (to.meta.requiresComplete) {
    if (!store.getters["flow/canShowResult"]) {
      if (!store.getters["session/isAuthenticated"]) return next({ name: "start" });
      return next({ name: store.getters["flow/furthestStep"] });
    }
    return next();
  }

  if (!store.getters["session/isAuthenticated"]) return next({ name: "start" });

  if (to.meta.step) {
    const furthest = store.getters["flow/furthestStep"];
    if (FLOW_ORDER.indexOf(to.meta.step) > FLOW_ORDER.indexOf(furthest)) {
      return next({ name: furthest });
    }
  }

  return next();
});

/** Remember the furthest step reached, so a reload can offer to resume there. */
router.afterEach((to) => {
  if (to.meta && to.meta.step) store.commit("flow/SET_RESUME_AT", to.name);
});

export default router;
